import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import { PageSkeleton } from '@/components/layout/page-skeleton';
import { MeetingWrapperIndex } from '@/pages/meeting/MeetingWrapperIndex';
import { MobilePrejoinSkeleton } from '@/pages/meeting/components/pre-join-mobile/MobilePrejoinSkeleton';
import { currentMobilePlatform } from '@/utils/mobile-platform';
import { rememberOutsideEscape } from '@/pages/meeting/components/MediaGuidance/outside-flag';
import { clearedMeetingPanelSearch, meetingPanelSearchFields } from '@/pages/meeting/panels/panel-decision';
import { hasPanelSearch, readPanelSearch, readStampedSurface } from '@/pages/meeting/panels/useMeetingPanels';

/**
 * The router parses a search value with `JSON.parse`, so `?outside=1` arrives as the
 * number 1 and `?instant=true` as a boolean. A flag is read as text either way.
 */
const flagSchema = z.coerce.string().optional();

const searchSchema = z.object({
	p: z.string().optional(),
	instant: flagSchema,
	/** Set by the link that left an app's own browser. Read once, then taken out of the address. */
	outside: flagSchema,
	// The panel params are declared once, next to the module that reads and writes them.
	...meetingPanelSearchFields,
});

export const Route = createFileRoute('/join/$meetingId')({
	validateSearch: searchSchema,
	// Without this the router keeps the page mounted across /join/A -> /join/B, and meeting A's layout store with it.
	remountDeps: ({ params }) => params.meetingId,
	// Runs before the page renders, so the flag is remembered before anything reads the environment.
	beforeLoad: ({ search }) => {
		if (search.outside === '1') rememberOutsideEscape();
	},
	component: JoinRoute,
	pendingComponent: JoinRoutePending,
});

/** A phone sees the pre-join's own skeleton while the page loads, not the app's dashboard one. */
function JoinRoutePending() {
	return currentMobilePlatform() ? <MobilePrejoinSkeleton /> : <PageSkeleton />;
}

function JoinRoute() {
	useDroppedOutsideFlag();
	useDroppedPanelParams();
	return <MeetingWrapperIndex />;
}

/** Keep the address clean: the flag has been remembered, so it does not belong in a shared link. */
function useDroppedOutsideFlag(): void {
	const navigate = useNavigate();
	const outside = Route.useSearch({ select: (search) => search.outside });

	useEffect(() => {
		if (outside === undefined) return;
		void navigate({ to: '.', search: (previous) => ({ ...previous, outside: undefined }), replace: true });
	}, [navigate, outside]);
}

/**
 * An open panel is a transient bit of this session, not addressable content: a copied link
 * and a reload both have to land in a clean meeting.
 *
 * What tells the two apart from a panel the user just opened is the entry stamp: every entry
 * this session pushed for a panel carries one, and nothing else ever does - not a deep link,
 * and not an entry restored from before a reload, whose stamp names a session that is gone.
 * So the strip is a function of the address alone, with no mount to key it to and nothing to
 * eat: it can run as often as it likes, including twice under StrictMode.
 */
function useDroppedPanelParams(): void {
	const navigate = useNavigate();
	const isForeignEntry = useRouterState({
		select: (state) =>
			hasPanelSearch(readPanelSearch(state.location.search)) && readStampedSurface(state.location.state) === undefined,
	});

	useEffect(() => {
		if (!isForeignEntry) return;
		void navigate({ to: '.', search: (previous) => ({ ...previous, ...clearedMeetingPanelSearch }), replace: true });
	}, [navigate, isForeignEntry]);
}
